import { Button, Spinner } from '@heroui/react';
import { SquareDashed } from 'iconoir-react';

import { agentLogDrawerActionFooterClassName } from './agentLogDrawerAccordionStyles';
import {
  DatasetSelectSection,
  TestCaseEditor,
  TestCaseSidebar
} from '../CollectExecutionDrawer';

import type { AgentExecutionDetail } from '~/api/agentLogs/types';
import i18n from '~/common/helpers/i18n';
import { useAgentLogCollectPanelState } from '~/scenes/Monitor/scenes/AgentLogs/hooks/useAgentLogCollectPanelState';

type AgentLogCollectPanelProps = {
  realm: string;
  detail: AgentExecutionDetail;
  onClose: () => void;
};

export function AgentLogCollectPanel({
  realm,
  detail,
  onClose
}: AgentLogCollectPanelProps) {
  const {
    datasets,
    datasetsLoading,
    selectedDatasetId,
    setSelectedDatasetId,
    selection,
    setSelection,
    testCases,
    datasetEvals,
    editorValues,
    editorSeed,
    nameError,
    handleEditorChange,
    setIsEditorValid,
    handleConfirm,
    isPending,
    canSave,
    showEmptyState,
    showLoadingState,
    showWorkspace
  } = useAgentLogCollectPanelState({ realm, detail });

  return (
    <div
      className='box-with-gradient-border relative flex h-full min-w-0 flex-1 flex-col bg-surface'
      data-testid='monitor-agent-logs-collect-panel'
    >
      <div className='flex flex-1 flex-col gap-6 overflow-auto px-6 py-4'>
        <DatasetSelectSection
          datasets={datasets}
          selectedDatasetId={selectedDatasetId}
          isLoading={datasetsLoading}
          onDatasetChange={setSelectedDatasetId}
          learnMoreHref={
            i18n.t('agentConnector.documentationSection.docsLink') as string
          }
        />

        {showEmptyState ? (
          <div
            className='flex flex-1 flex-col items-center justify-center gap-4 py-16 text-center'
            data-testid='monitor-agent-logs-collect-empty'
          >
            <div className='flex size-16 items-center justify-center rounded-[9.6px] bg-surface-container'>
              <SquareDashed className='icon-large text-on-surface-variant' />
            </div>
            <p className='max-w-md text-body-regular-md text-on-surface-variant'>
              {i18n.t('scenes.monitor.agentLogs.collect.emptyState')}
            </p>
          </div>
        ) : null}

        {showLoadingState ? (
          <div
            className='flex flex-1 items-center justify-center'
            data-testid='monitor-agent-logs-collect-loading'
          >
            <Spinner size='lg' />
          </div>
        ) : null}

        {showWorkspace ? (
          <div
            className='flex min-h-0 flex-1 gap-7'
            data-testid='monitor-agent-logs-collect-workspace'
          >
            <TestCaseSidebar
              testCases={testCases}
              selection={selection}
              isLoading={false}
              onSelectNew={() => setSelection({ type: 'new' })}
              onSelectExisting={experimentId =>
                setSelection({ type: 'existing', experimentId })
              }
            />
            <div className='flex min-w-0 flex-1 flex-col'>
              <TestCaseEditor
                formKey={`seed-${editorSeed}`}
                values={editorValues}
                datasetEvals={datasetEvals}
                onChange={handleEditorChange}
                onValidityChange={setIsEditorValid}
                nameError={nameError}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className={agentLogDrawerActionFooterClassName}>
        <Button
          variant='bordered'
          onPress={onClose}
          className='border-on-surface text-on-surface'
          data-testid='monitor-agent-logs-collect-cancel'
        >
          {i18n.t('scenes.monitor.agentLogs.collect.cancel')}
        </Button>
        <Button
          color='primary'
          onPress={handleConfirm}
          isDisabled={!canSave || isPending}
          isLoading={isPending}
          data-testid='monitor-agent-logs-collect-confirm'
        >
          {i18n.t('scenes.monitor.agentLogs.collect.save')}
        </Button>
      </div>
    </div>
  );
}
